import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import DataObjectIcon from "@mui/icons-material/DataObject";
import BlockIcon from "@mui/icons-material/Block";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import SendIcon from "@mui/icons-material/Send";
import YouTubeIcon from "@mui/icons-material/YouTube";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import LibraryEditForm from "./LibraryEditForm";
import LibraryAddStepper from "./LibraryAddStepper";
import LibraryPickForm from "./LibraryPickForm";

import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";
import { columns, defaultTableOptions } from "../../../../helper-functions/table/libraries";
import { createCookieStepper } from "../../../../helper-functions/api/cookieQueries";
import {
    approveLibrary,
    approveLibraries,
    disapproveLibrary,
    disapproveLibraries,
    createLibrary,
    deleteLibraries,
    queryLibraries,
    retryLibrary,
    retryLibraries,
    retryLibraryMetadata,
    updateLibrary,
    startProcessLibrary,
    stopProcessLibrary,
    sendWebhook,
    recheckLibraryCatalog
} from "../../../../helper-functions/api/libraryQueries";

import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing libraries.
 * If no data found, show error message instead.
 */
export default function LibrariesTable(props) {
    const libraries = props.backend.libraries;

    const isProcessing = libraries.data.some((request) => request.status === "IN_PROGRESS");

    const [disabled, setDisabled] = React.useState(false);

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new library",
            component: <LibraryAddStepper {...props} callback={(confirm, options) => handleStepper(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete libraries",
            message: `Are you sure you want to delete the ${libraries.selected.length} selected libraries?
            This action cannot be undone.`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => showDeleteResourcesDialog(confirm)
        });
    };

    const showDeleteResourcesDialog = (confirm) => {
        if (!confirm) return;

        props.showDialog({
            title: "Delete library resources?",
            message: `Would you like all resources related to this library to be deleted as well?
            By deleting the related resources, any transcripts and quotes will be lost.`,
            actionTitle: "Also delete related resources",
            actionColor: "error",
            cancelTitle: "Only delete libraries",
            cancelColor: "warning",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit library",
            message: "Click 'Update' to confirm changes.",
            component: <LibraryEditForm {...props} row={libraries.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const showApproveDialog = () => {
        const count = libraries.selected.length;
        const handle = count > 1 ? handleMultiApprove : handleApprove;

        props.showDialog({
            title: count > 1 ? "Approve libraries" : "Approve library",
            message: `Are you sure you want to approve the ${count} selected ${count === 1 ? "library" : "libraries"}?
            After approval, each library will soon begin further processing by querying and adding resources.`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const showDisapproveDialog = () => {
        const count = libraries.selected.length;
        const handle = count > 1 ? handleMultiDisapprove : handleDisapprove;

        props.showDialog({
            title: count > 1 ? "Disapprove libraries" : "Disapprove library",
            message: `Are you sure you want to disapprove the ${count} selected ${count === 1 ? "library" : "libraries"}?
            After disapproval, each library will not be processed further.`,
            actionTitle: "Disapprove",
            actionColor: "error",
            callback: (confirm) => handle(confirm)
        });
    };

    const showRetryDialog = () => {
        const count = libraries.selected.length;
        const handle = count > 1 ? handleMultiRetry : handleRetry;

        props.showDialog({
            title: count > 1 ? "Retry libraries" : "Retry library",
            message: `Are you sure you want to retry the processing of the ${count} selected ${count === 1 ? "library" : "libraries"}?
            This will continue the processing of each library from the last step.`,
            actionTitle: "Retry",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const showStartProcessDialog = () => {
        props.showDialog({
            title: "Start processing libraries",
            message: `Are you sure you want to process the queued libraries?
            This will start the processing ahead of schedule and may take some time.`,
            actionTitle: "Begin processing",
            actionColor: "success",
            callback: (confirm) => handleStartProcess(confirm)
        });
    };

    const showStopProcessDialog = () => {
        props.showDialog({
            title: "Stop processing libraries",
            message: `Are you sure you want to stop library processing?
            The library in progress stops, and every library still queued returns to awaiting approval. Nothing is marked as an error.`,
            actionTitle: "Stop processing",
            actionColor: "error",
            callback: (confirm) => handleStopProcess(confirm)
        });
    };

    const handleStepper = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row, cookieRow } = options;

        // Create cookie before sending the library request.
        if (cookieRow?.type && cookieRow?.text) {
            const body = createUploadObject(cookieRow, ["form"]);
            createCookieStepper(props, { body }, (response) => {
                handleCreate({ row });
            });
        } else {
            handleCreate({ row });
        }
    };

    const handleCreate = (options = {}) => {
        const { row } = options;

        const body = createUploadObject(row, ["form"]);

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING, duration: 10000 });
        createLibrary(props, { body });
    };

    const handleDelete = (confirm) => {
        const ids = libraries.selected.map((library) => library.id);
        deleteLibraries(props, { body: { ids }, ids, deleteResources: confirm });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form"]);

        updateLibrary(props, { body, id: row.id });
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        const ids = libraries.selected.map((library) => library.id);
        approveLibrary(props, { id: ids?.[0] });
    };

    const handleMultiApprove = (confirm) => {
        if (!confirm) return;

        const ids = libraries.selected.map((library) => library.id);
        approveLibraries(props, { body: { ids } });
    };

    const handleDisapprove = (confirm) => {
        if (!confirm) return;

        const ids = libraries.selected.map((library) => library.id);
        disapproveLibrary(props, { id: ids?.[0] });
    };

    const handleMultiDisapprove = (confirm) => {
        if (!confirm) return;

        const ids = libraries.selected.map((library) => library.id);
        disapproveLibraries(props, { body: { ids } });
    };

    const handleRetry = (confirm) => {
        if (!confirm) return;

        const ids = libraries.selected.map((library) => library.id);
        retryLibrary(props, { id: ids?.[0] });
    };

    const handleMultiRetry = (confirm) => {
        if (!confirm) return;

        const ids = libraries.selected.map((library) => library.id);
        retryLibraries(props, { body: { ids } });
    };

    const handleStartProcess = (confirm) => {
        if (!confirm) return;
        setDisabled(true);

        startProcessLibrary(props);

        setTimeout(() => setDisabled(false), 10000); // This query can take a bit of time in the backend
    };

    const handleStopProcess = (confirm) => {
        if (!confirm) return;
        setDisabled(true);

        stopProcessLibrary(props);

        setTimeout(() => setDisabled(false), 10000); // This query can take a bit of time in the backend
    };

    const showSendWebhookDialog = () => {
        props.showDialog({
            title: getLanguage().LIBRARY_WEBHOOK_DIALOG_TITLE,
            message: getLanguage().LIBRARY_WEBHOOK_DIALOG_MESSAGE,
            component: (
                <LibraryPickForm
                    {...props}
                    multiselect
                    confirmTitle={getLanguage().LIBRARY_WEBHOOK_SEND}
                    callback={(confirm, options) => handleSendWebhook(confirm, options)}
                />
            )
        });
    };

    const handleSendWebhook = (confirm, options = {}) => {
        props.setOpenDialog(false);
        if (!confirm) return;
        setDisabled(true);

        sendWebhook(props, { libraryIds: options.libraryIds });

        setTimeout(() => setDisabled(false), 10000);
    };

    const showRecheckDialog = () => {
        props.showDialog({
            title: getLanguage().LIBRARY_RECHECK_DIALOG_TITLE,
            message: getLanguage().LIBRARY_RECHECK_DIALOG_MESSAGE,
            component: (
                <LibraryPickForm
                    {...props}
                    confirmTitle={getLanguage().LIBRARY_RECHECK_START}
                    callback={(confirm, options) => handleRecheck(confirm, options)}
                />
            )
        });
    };

    const handleRecheck = (confirm, options = {}) => {
        props.setOpenDialog(false);
        if (!confirm) return;

        recheckLibraryCatalog(props, { id: options.libraryId });
    };

    const handleRetryMetadata = () => {
        const ids = libraries.selected.map((library) => library.id);

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING, duration: 10000 });
        retryLibraryMetadata(props, { id: ids?.[0] });
    };

    return (
        <PaperFragment
            className="tour-libraries-3 tour-libraries-4 tour-libraries-5 tour-libraries-6"
            title={
                <StackedHeader
                    {...props}
                    title="Libraries"
                    subtitle="View and manage libraries."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected libraries",
                            disabled: libraries.selected.length < 1,
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected library",
                            onClick: showUpdateDialog,
                            disabled: libraries.selected.length !== 1
                        },
                        {
                            icon: <CheckIcon />,
                            tooltip: "Approve selected library",
                            onClick: showApproveDialog,
                            disabled: libraries.selected.length < 1 || libraries.selected.some((library) => library.status !== "AWAITING_APPROVAL")
                        },
                        {
                            icon: <BlockIcon />,
                            tooltip: "Disapprove selected library",
                            onClick: showDisapproveDialog,
                            disabled: libraries.selected.length < 1 || libraries.selected.some((library) => !["IN_QUEUE", "PROCESSED"].includes(library.status))
                        },
                        {
                            icon: <ReplayIcon />,
                            tooltip: "Retry selected library",
                            onClick: showRetryDialog,
                            disabled: libraries.selected.length < 1 || libraries.selected.some((library) => library.status !== "ERROR")
                        },
                        {
                            icon: <DataObjectIcon />,
                            tooltip: "Retry querying selected library metadata",
                            onClick: handleRetryMetadata,
                            disabled: libraries.selected.length !== 1 || libraries.selected[0]?.status !== "AWAITING_APPROVAL"
                        }
                    ]}
                    buttons={[
                        {
                            disabled: disabled,
                            icon: <YouTubeIcon />,
                            onClick: showRecheckDialog,
                            tooltip: getLanguage().LIBRARY_TOOLTIP_RECHECK,
                            size: "small",
                            className: "button-small"
                        },
                        {
                            disabled: disabled,
                            icon: <SendIcon />,
                            onClick: showSendWebhookDialog,
                            tooltip: getLanguage().LIBRARY_TOOLTIP_WEBHOOK,
                            size: "small",
                            className: "button-small"
                        },
                        {
                            disabled: disabled,
                            icon: isProcessing ? <StopCircleIcon /> : <PlayCircleIcon />,
                            onClick: isProcessing ? showStopProcessDialog : showStartProcessDialog,
                            tooltip: isProcessing ? "Stop processing libraries" : "Start processing libraries",
                            size: "small",
                            className: "button-small"
                        },
                        {
                            icon: <AddIcon />,
                            title: "New library",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={libraries}
                    columns={columns(props)}
                    query={(options) => queryLibraries(props, options)}
                    empty={getLanguage().NO_LIBRARIES_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
